import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { RecurringScope } from '@/scheduling/components/appointments/edit-recurring-scope-dialog';

/**
 * Which sessions of a device COURSE a reschedule reaches (PROD-2526).
 *
 * The same three choices, in the same words and order, as the practitioner
 * series menu (`series-appointment-row-actions.tsx`: "Reschedule this
 * appointment / this and following / all in the series"), so a device course
 * and a practitioner series are rescheduled the same way. It is asked when a
 * session of a course is moved from the Book-a-device rail; the answer becomes
 * `editRecurring` on `PUT /api/Appointment/Resource/{id}` (PROD-2383).
 *
 * What travels to the other sessions is the TIME: the server moves each reached
 * session to the new time on its own day (`RecurringAppointmentOrchestrator`,
 * `dataUpdate: ['start', 'end']`), exactly as a practitioner series reschedule
 * does. A PRODUCT change is not carried to the other sessions by that path, so
 * when the product also changed only "this appointment" is offered, with the
 * reason on screen, rather than letting the series choice silently drop it.
 */
export interface DeviceSeriesScopeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The product changed in the same edit — only this session can take it. */
  productChanged: boolean;
  isSubmitting: boolean;
  onConfirm: (scope: RecurringScope) => void;
}

const OPTIONS: { value: RecurringScope; label: string; description: string }[] = [
  {
    value: 'this-only',
    label: 'Reschedule this appointment',
    description: 'Move just this session',
  },
  {
    value: 'following',
    label: 'Reschedule this and following appointments',
    description: 'Move this session and every later one in the course to the new time',
  },
  {
    value: 'all',
    label: 'Reschedule all appointments in the series',
    description: 'Move every upcoming session in the course to the new time',
  },
];

export function DeviceSeriesScopeDialog({
  open,
  onOpenChange,
  productChanged,
  isSubmitting,
  onConfirm,
}: DeviceSeriesScopeDialogProps) {
  const [scope, setScope] = useState<RecurringScope>('this-only');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[var(--dialog-width-confirm)]" data-testid="device-series-scope-dialog">
        <DialogHeader>
          <DialogTitle>This is a recurring appointment</DialogTitle>
        </DialogHeader>
        <fieldset className="flex flex-col gap-2 border-0 p-0 m-0">
          <legend className="sr-only">Which sessions to reschedule</legend>
          {OPTIONS.map((opt) => {
            const disabled = productChanged && opt.value !== 'this-only';
            const active = scope === opt.value;
            return (
              <label
                key={opt.value}
                className={cn(
                  'block cursor-pointer rounded-card border p-3 transition-colors',
                  'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-primary has-[:focus-visible]:ring-offset-1',
                  active ? 'border-brand-primary bg-[var(--accent-active-bg)]' : 'border-[var(--surface-line)] hover:bg-grey-100',
                  disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <input
                  type="radio"
                  name="device-series-scope"
                  value={opt.value}
                  checked={active}
                  disabled={disabled}
                  onChange={() => setScope(opt.value)}
                  className="sr-only"
                />
                <span className={cn('block text-sm font-medium', active ? 'text-brand-primary' : 'text-text-primary')}>
                  {opt.label}
                </span>
                <span className="block text-xs text-text-muted mt-1">{opt.description}</span>
              </label>
            );
          })}
        </fieldset>
        {productChanged ? (
          <p className="text-xs text-text-muted" data-testid="device-series-scope-product-note">
            The product changed too. A product change applies to this session only, so the other
            sessions can't be moved in the same save.
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(scope)} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
